import { forwardRef, useId } from 'react';
import cx from 'classnames';
import './Textarea.css';

/**
 * QHS Textarea v2
 * ---------------
 * Multi-line companion to Input. Same label / helper / error / success
 * messaging slots and the same wrapper class so a Textarea sitting next
 * to an Input in a FieldGrid lines up visually.
 *
 *   <Textarea label="Comments" rows={4} value={v} onChange={...} />
 *
 * Props match Input where it makes sense (label, helper, error, success,
 * required, fullWidth, size). Native textarea props (rows, maxLength,
 * placeholder, name, value, onChange, disabled) pass through.
 */
const Textarea = forwardRef(function Textarea(
  {
    label,
    helper,
    error,
    success,
    required,
    fullWidth = false,
    size = 'md',
    rows = 4,
    resize = 'vertical',
    className,
    id,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;

  const wrapperClasses = cx(
    'qhs-field',
    `qhs-field--${size}`,
    'qhs-field--textarea',
    fullWidth && 'qhs-field--full',
    error && 'qhs-field--error',
    success && 'qhs-field--success',
    className
  );

  const message = error || success || helper;
  const messageType = error ? 'error' : success ? 'success' : 'helper';

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className='qhs-field__label'>
          {label}
          {required && (
            <span className='qhs-field__req' aria-hidden='true'>
              *
            </span>
          )}
        </label>
      )}

      <div className='qhs-field__wrap'>
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          className='qhs-field__input qhs-field__textarea'
          style={{ resize }}
          aria-invalid={!!error || undefined}
          aria-describedby={message ? `${inputId}-msg` : undefined}
          {...rest}
        />
      </div>

      {message && (
        <span id={`${inputId}-msg`} className={`qhs-field__msg qhs-field__msg--${messageType}`}>
          {message}
        </span>
      )}
    </div>
  );
});

export default Textarea;
